<script lang="ts">
  // HR > Push alerts — who to watch, which punches to send, and a one-shot
  // test button so this device can grant notification permission.
  //
  // There is no log here. The bell (and the OS notification, once push is
  // enabled) is the inbox; this page is just the preference + permission
  // setup. See internal/api/handlers/hr_punch_alerts.go.
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { hrPunchAlertsApi, hrUsersApi } from '$lib/api/hr';
  import { enablePush, pushSupported, currentSubscription } from '$lib/api/push';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import type { PunchAlertEvents, PunchAlertPrefs, PunchAlertScope } from '$lib/types';

  const queryClient = useQueryClient();

  const prefsQuery = createQuery(() => ({
    queryKey: ['punch-alerts'],
    queryFn: () => hrPunchAlertsApi.get()
  }));
  const usersQuery = createQuery(() => ({
    queryKey: ['hr-users'],
    queryFn: () => hrUsersApi.list()
  }));

  let enabled = $state(false);
  let scope = $state<PunchAlertScope>('all');
  let events = $state<PunchAlertEvents>('all');
  let selected = $state<Record<number, boolean>>({});
  let hydrated = $state(false);
  let saving = $state(false);
  let testBusy = $state(false);
  let deviceStatus = $state<'checking' | 'unsupported' | 'denied' | 'ready' | 'needs_permission'>('checking');

  $effect(() => {
    const p = prefsQuery.data;
    if (!p || hydrated) return;
    enabled = p.enabled;
    scope = p.scope;
    events = p.events;
    const next: Record<number, boolean> = {};
    for (const id of p.employee_ids) next[id] = true;
    selected = next;
    hydrated = true;
  });

  async function checkDevice() {
    if (!pushSupported()) {
      deviceStatus = 'unsupported';
      return;
    }
    if (Notification.permission === 'denied') {
      deviceStatus = 'denied';
      return;
    }
    if (Notification.permission === 'granted' && (await currentSubscription())) {
      deviceStatus = 'ready';
      return;
    }
    deviceStatus = 'needs_permission';
  }
  checkDevice();

  const people = $derived((usersQuery.data ?? []).filter((u) => u.active && !u.attendance_exempt).sort((a, b) => a.name.localeCompare(b.name)));

  function selectedIDs(): number[] {
    return Object.entries(selected)
      .filter(([, on]) => on)
      .map(([id]) => Number(id));
  }

  async function persist(next: Pick<PunchAlertPrefs, 'enabled' | 'scope' | 'events' | 'employee_ids'>) {
    saving = true;
    try {
      const saved = await hrPunchAlertsApi.save(next);
      queryClient.setQueryData(['punch-alerts'], saved);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      saving = false;
    }
  }

  function setEnabled(on: boolean) {
    enabled = on;
    persist({ enabled: on, scope, events, employee_ids: selectedIDs() });
  }

  function setScope(next: PunchAlertScope) {
    scope = next;
    persist({ enabled, scope: next, events, employee_ids: selectedIDs() });
  }

  function setEvents(next: PunchAlertEvents) {
    events = next;
    persist({ enabled, scope, events: next, employee_ids: selectedIDs() });
  }

  function togglePerson(id: number) {
    selected = { ...selected, [id]: !selected[id] };
    persist({ enabled, scope, events, employee_ids: selectedIDs() });
  }

  async function sendTest() {
    testBusy = true;
    try {
      if (deviceStatus !== 'ready') {
        const result = await enablePush();
        if (!result.ok) {
          toast.error(result.error || 'Could not enable push notifications.');
          await checkDevice();
          return;
        }
      }
      await hrPunchAlertsApi.test();
      await checkDevice();
      toast.success('Test notification sent — check this device.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      testBusy = false;
    }
  }

  function deviceCopy(): string {
    switch (deviceStatus) {
      case 'unsupported':
        return 'This browser cannot receive push notifications.';
      case 'denied':
        return 'Notifications are blocked for this site. Allow them in the browser settings, then try again.';
      case 'ready':
        return 'This device can receive punch alerts.';
      case 'needs_permission':
        return 'Allow notifications once so punch alerts can reach this browser.';
      default:
        return 'Checking this device…';
    }
  }
</script>

<div class="tym-page">
  <div class="tym-card mb-4 p-5">
    <p class="tym-section-title text-base">This device</p>
    <p class="tym-section-sub">{deviceCopy()}</p>
    <button
      type="button"
      class="btn-primary mt-3.5"
      disabled={testBusy || deviceStatus === 'unsupported' || deviceStatus === 'denied' || deviceStatus === 'checking'}
      onclick={sendTest}
    >
      {#if testBusy}
        Sending…
      {:else if deviceStatus === 'ready'}
        Send a test notification
      {:else}
        Enable &amp; send a test
      {/if}
    </button>
  </div>

  {#if prefsQuery.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if prefsQuery.isError}
    <div class="tym-error-state">Could not load punch-alert settings.</div>
  {:else}
    {@const hours = prefsQuery.data}
    <div class="tym-card p-5">
      <label class="flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          class="mt-0.5 h-4 w-4 accent-toyama-teal"
          checked={enabled}
          disabled={saving}
          onchange={(e) => setEnabled(e.currentTarget.checked)}
        />
        <span>
          <span class="block font-medium text-navy">Send me punch alerts</span>
          <span class="tym-section-sub !mt-0.5">Off until you turn it on — nobody is notified by default.</span>
        </span>
      </label>

      <div class="mt-5 {enabled ? '' : 'pointer-events-none opacity-50'}">
        <p class="mb-2 text-[12px] font-semibold uppercase tracking-[.06em] text-dark-gray">Who</p>
        <div class="flex flex-wrap gap-1.5">
          <button type="button" class="tym-chip {scope === 'all' ? 'active' : ''}" onclick={() => setScope('all')}>
            All employees
          </button>
          <button type="button" class="tym-chip {scope === 'selected' ? 'active' : ''}" onclick={() => setScope('selected')}>
            Specific people
          </button>
        </div>

        {#if scope === 'selected'}
          <ul class="mt-3 max-h-64 space-y-1 overflow-y-auto">
            {#each people as u (u.id)}
              <li>
                <label class="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-surface-2">
                  <input
                    type="checkbox"
                    class="h-4 w-4 accent-toyama-teal"
                    checked={!!selected[u.id]}
                    onchange={() => togglePerson(u.id)}
                  />
                  <span class="min-w-0 truncate text-[13px] text-navy">{u.name}</span>
                </label>
              </li>
            {:else}
              <li class="tym-empty-state !p-0 text-left">No active employees to pick.</li>
            {/each}
          </ul>
        {/if}

        <p class="mb-2 mt-5 text-[12px] font-semibold uppercase tracking-[.06em] text-dark-gray">What</p>
        <div class="flex flex-wrap gap-1.5">
          <button type="button" class="tym-chip {events === 'all' ? 'active' : ''}" onclick={() => setEvents('all')}>
            Every punch in and out
          </button>
          <button type="button" class="tym-chip {events === 'exceptions' ? 'active' : ''}" onclick={() => setEvents('exceptions')}>
            Late in / early out only
          </button>
        </div>
        {#if hours}
          <p class="tym-section-sub mt-2">
            Late is after {hours.late_after}, early is before {hours.early_before}. Field-work punches are skipped in
            the late/early filter.
          </p>
        {/if}
      </div>
    </div>
  {/if}
</div>
